import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiPhone, FiMail, FiMapPin, FiClock } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import logo from '../lasmari-wordmark-red.png';

// Brand red (sampled from the official Lasmari Chauffeurs wordmark)
const BRAND_RED = '#BA1609';

// Canonical contact details
const CONTACT = {
  phone: '+30 2663098633',
  phoneTel: 'tel:+302663098633',
  whatsapp: '+44 7404 560313',
  whatsappUrl: 'https://wa.me/447404560313',
  email: 'contact@lasmari.gr',
};

const Navigation = ({ currentSection = 'home' }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navigationItems = useMemo(() => [
    { id: 'home', label: 'Home', path: '/' },
    { id: 'airport-transfers', label: 'Airport Transfers', path: '/airport-transfers' },
    { id: 'port-transfers', label: 'Port Transfers', path: '/port-transfers' },
    { id: 'tours', label: 'Tours', path: '/private-tours' },
    { id: 'chauffeur', label: 'Chauffeur', path: '/hourly-chauffeur' },
    { id: 'faq', label: 'FAQ', path: '/faq' },
  ], []);

  const forceCloseMenu = useCallback(() => {
    if (isMenuOpen) {
      setIsClosing(true);
      setTimeout(() => {
        setIsMenuOpen(false);
        setIsClosing(false);
      }, 300);
    }
    document.body.classList.remove('menu-open');
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
  }, [isMenuOpen]);

  const handleNavClick = useCallback((item) => {
    if (window.innerWidth < 1024) {
      forceCloseMenu();
    }

    // If on homepage and clicking "Home", scroll to top
    if (isHomePage && item.id === 'home') {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
    }
    // All other items navigate via Link (handled by react-router)
  }, [isHomePage, forceCloseMenu]);

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event) => {
      if (!event.target.closest('.mobile-menu') && !event.target.closest('.menu-toggle')) {
        forceCloseMenu();
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        forceCloseMenu();
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    document.body.classList.add('menu-open');
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.classList.remove('menu-open');
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isMenuOpen, forceCloseMenu]);

  const toggleMenu = useCallback(() => {
    if (!isMenuOpen) {
      setIsMenuOpen(true);
      setIsClosing(false);
    } else {
      setIsClosing(true);
      setTimeout(() => {
        setIsMenuOpen(false);
        setIsClosing(false);
      }, 300);
    }
  }, [isMenuOpen]);

  const isActive = (item) => {
    if (item.path === '/') return location.pathname === '/';
    return location.pathname.startsWith(item.path);
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-[100] transition-shadow duration-300 ${scrolled ? 'shadow-lg shadow-black/30' : ''}`}>

        {/* Top utility bar */}
        <div className="text-white border-b border-white/10" style={{ backgroundColor: BRAND_RED }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center md:justify-between h-8 sm:h-9 text-[11px] sm:text-xs font-medium tracking-wide">
              {/* Location + hours (desktop) */}
              <div className="hidden md:flex items-center gap-6 opacity-80">
                <span className="flex items-center gap-1.5">
                  <FiMapPin className="opacity-80" aria-hidden /> Apraos · North Corfu
                </span>
                <span className="flex items-center gap-1.5">
                  <FiClock className="opacity-80" aria-hidden /> 24/7 Service
                </span>
              </div>
              {/* Phone + WhatsApp + email */}
              <div className="flex items-center gap-4 sm:gap-6">
                <a href={CONTACT.phoneTel} className="flex items-center gap-1.5 hover:opacity-60 transition-opacity">
                  <FiPhone className="opacity-80" aria-hidden /> {CONTACT.phone}
                </a>
                <a
                  href={CONTACT.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 hover:opacity-60 transition-opacity"
                >
                  <FaWhatsapp className="opacity-90" aria-hidden />
                  <span className="hidden sm:inline">{CONTACT.whatsapp}</span>
                  <span className="sm:hidden">WhatsApp</span>
                </a>
                <a href={`mailto:${CONTACT.email}`} className="hidden md:flex items-center gap-1.5 hover:opacity-60 transition-opacity">
                  <FiMail className="opacity-80" aria-hidden /> {CONTACT.email}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Main navigation */}
        <div className="relative bg-white border-t border-black/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20 lg:h-24">

              {/* Logo */}
              <Link
                to="/"
                className="flex items-center cursor-pointer z-10"
              >
                <img
                  src={logo}
                  alt="Lasmari Chauffeurs"
                  className="h-16 lg:h-[72px] w-auto"
                />
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center space-x-7">
                {navigationItems.map((item) => (
                  <Link
                    key={item.id}
                    to={item.path}
                    onClick={() => handleNavClick(item)}
                    className={`relative text-[13px] font-medium uppercase tracking-[0.14em] transition-opacity duration-200 ${
                      isActive(item) ? 'opacity-100' : 'opacity-70 hover:opacity-100'
                    }`}
                    style={{ color: BRAND_RED }}
                  >
                    {item.label}
                    <span
                      className={`absolute -bottom-1.5 left-0 h-px transition-all duration-300 ${
                        isActive(item) ? 'w-full' : 'w-0'
                      }`}
                      style={{ backgroundColor: BRAND_RED }}
                    />
                  </Link>
                ))}
                <Link
                  to="/booking"
                  className="px-6 py-2 rounded-full text-xs font-semibold uppercase tracking-[0.12em] text-white hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: BRAND_RED }}
                >
                  Book Now
                </Link>
              </div>

              {/* Mobile: Book Now + Menu Toggle */}
              <div className="lg:hidden flex items-center gap-3 z-10">
                <Link
                  to="/booking"
                  className="px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wide text-white"
                  style={{ backgroundColor: BRAND_RED }}
                >
                  Book Now
                </Link>
                <button
                  className="menu-toggle relative transition-all duration-300 flex items-center justify-center bg-transparent border-0 outline-none focus:outline-none transform hover:scale-110 active:scale-95 hover:opacity-70"
                  onClick={toggleMenu}
                  aria-label="Toggle menu"
                  aria-expanded={isMenuOpen}
                  style={{
                    color: BRAND_RED,
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation',
                    userSelect: 'none',
                    minWidth: '48px',
                    minHeight: '48px',
                    padding: '10px',
                    cursor: 'pointer',
                    pointerEvents: 'auto'
                  }}
                >
                  <span
                    className={`transition-all duration-300 ease-out transform ${
                      isMenuOpen ? 'rotate-180 scale-110' : 'rotate-0 scale-100'
                    }`}
                    style={{ pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    {isMenuOpen ? <FiX size={28} /> : <FiMenu size={28} />}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Menu */}
          {(isMenuOpen || isClosing) && (
            <div
              className={`lg:hidden mobile-menu absolute top-full left-0 right-0 z-[100] bg-white border-t border-black/5 shadow-xl transform transition-all duration-300 ease-out ${
                isClosing
                  ? '-translate-y-2 opacity-0'
                  : 'translate-y-0 opacity-100'
              }`}
            >
              <div className="max-w-7xl mx-auto px-4 py-5 space-y-1">
                {navigationItems.map((item, index) => (
                  <Link
                    key={item.id}
                    to={item.path}
                    onClick={() => {
                      handleNavClick(item);
                    }}
                    className={`block w-full text-left py-3.5 px-5 rounded-lg uppercase tracking-[0.12em] text-sm font-medium transition-all duration-200 transform ${
                      isActive(item)
                        ? 'opacity-100 bg-black/[0.04]'
                        : 'opacity-70 hover:opacity-100 hover:bg-black/[0.03] active:bg-black/[0.05]'
                    } ${isClosing ? 'translate-x-4 opacity-0' : 'translate-x-0 opacity-100'}`}
                    style={{
                      color: BRAND_RED,
                      WebkitTapHighlightColor: 'transparent',
                      touchAction: 'manipulation',
                      userSelect: 'none',
                      transitionDelay: isClosing ? '0ms' : `${index * 50}ms`
                    }}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Navigation;
